// it doesn't make sense to export each thing in a separate file in this case, so turning off the warning here
/* eslint-disable react-refresh/only-export-components */
import type { EnvironmentClassEntry } from "@/hooks/use-environment-class-entries";
import type { PlainMessage } from "@bufbuild/protobuf";
import type { AuthenticatedWithRunnerResponse } from "@/queries/runner-queries";
import type { Runner } from "gitpod-next-api/gitpod/v1/runner_pb";
import { atom, useAtom } from "jotai";
import { atomEffect } from "jotai-effect";
import { useMemo } from "react";
import { useDebouncedCallback } from "use-debounce";
import { z } from "zod";
import { atomWithLazy } from "jotai/utils";
import type { ParseContextURLResponse } from "gitpod-next-api/gitpod/v1/runner_pb";
import type { PromptInputSubmission, RepoContext } from "@/components/ai/prompt-input/NewPromptInput";
import { IconCircleDotted } from "central-icons/IconCircleDotted";
import { IconArrowSplit } from "central-icons/IconArrowSplit";
import IconTestflight from "central-icons/IconTestflight";

export const ONA_ONBOARDING_PARAM = "ona-onboarding";

export const TASK_NAME = {
    automateEnvSetup: "automate-env-setup",
    fixBug: "fix-bug",
    boostTestCoverage: "boost-test-coverage",
} as const;
export type TaskName = (typeof TASK_NAME)[keyof typeof TASK_NAME];

export const TASK_DESCRIPTIONS = {
    [TASK_NAME.fixBug]: {
        taskName: TASK_NAME.fixBug,
        Icon: IconArrowSplit,
        iconColor: "#541500",
        iconBackgroundColor: "#FFDED4",
        title: "Find a critical bug and fix it.",
        description: "Find a bug in the codebase that looks important and fix it.",
        prompt: "Scan through the codebase to identify bugs that look important or impactful. Focus on issues that could affect functionality, performance, or user experience. Once you find a significant bug, create a new branch, implement a fix, write or update tests as needed, and commit the changes with a clear description.",
    },
    [TASK_NAME.boostTestCoverage]: {
        taskName: TASK_NAME.boostTestCoverage,
        Icon: IconTestflight,
        iconColor: "#9800F0",
        iconBackgroundColor: "#EFD4FF",
        title: "Improve test coverage of core business logic.",
        description: "Find key areas to cover with new and smarter tests.",
        prompt: "Scan the codebase to understand its main business logic. Then identify ONE untested function related to this business logic and write a single focused, useful unit test for it. Cover the main execution path and follow existing test patterns. Ensure the test passes.",
    },
    [TASK_NAME.automateEnvSetup]: {
        taskName: TASK_NAME.automateEnvSetup,
        Icon: IconCircleDotted,
        iconColor: "#0048FF",
        iconBackgroundColor: "#CAD7FA",
        title: "Automate env setup",
        description: "Create a fully working dev environment as code configuration.",
        prompt: `Set up a fully working development environment as code for the current environment using the devcontainer-setup skill.

Use the read_skill tool to load the "devcontainer-setup" skill and follow its workflow to:
1. Analyze the repository structure
2. Create devcontainer.json and automations.yaml
3. Rebuild the devcontainer and verify the environment

Do not create any documentation files.`,
    },
} as const;

export const ONBOARDING_STEP_SLUG = {
    getStartedWithTasks: "get-started-with-tasks",
    // TODO: change this to "connect-to-scm" when we support other SCMs
    connectToScm: "connect-to-github",
    selectRepository: "select-your-repository",
} as const;
export type OnboardingStepSlug = (typeof ONBOARDING_STEP_SLUG)[keyof typeof ONBOARDING_STEP_SLUG];

export const ONBOARDING_STEP = {
    [ONBOARDING_STEP_SLUG.connectToScm]: {
        index: 1,
        slug: ONBOARDING_STEP_SLUG.connectToScm,
    },
    [ONBOARDING_STEP_SLUG.selectRepository]: {
        index: 2,
        slug: ONBOARDING_STEP_SLUG.selectRepository,
    },
    [ONBOARDING_STEP_SLUG.getStartedWithTasks]: {
        index: 3,
        slug: ONBOARDING_STEP_SLUG.getStartedWithTasks,
    },
} as const;
export type OnboardingStep = (typeof ONBOARDING_STEP)[keyof typeof ONBOARDING_STEP];

export const ONBOARDING_TASK_MODE = {
    predefined: "predefined",
    custom: "custom",
} as const;
export type OnboardingTaskMode = (typeof ONBOARDING_TASK_MODE)[keyof typeof ONBOARDING_TASK_MODE];

export const SavedOnboardingStateSchema = z.object({
    currentStep: z.nativeEnum(ONBOARDING_STEP_SLUG),
    taskMode: z.nativeEnum(ONBOARDING_TASK_MODE),
    selectedTasks: z.array(z.nativeEnum(TASK_NAME)).nonempty(),
    customTaskPrompt: z.string(),
    selectedRepositoryUrl: z.string().nullable(),
});
export type SavedOnboardingState = z.infer<typeof SavedOnboardingStateSchema>;

export type AsyncQuerySuccess<T> = {
    type: "success";
    data: T;
};
export type AsyncQueryError = {
    type: "error";
    error: unknown;
};
export type AsyncQueryPending = {
    type: "pending";
};
export type AsyncQuery<T> = AsyncQuerySuccess<T> | AsyncQueryError | AsyncQueryPending;

export type ScmAuthStatusMap = Record<string, AsyncQuery<AuthenticatedWithRunnerResponse>>;

export type OnboardingTaskList = SavedOnboardingState["selectedTasks"];
export type OnboardingState = {
    currentStep: OnboardingStep;
    taskMode: OnboardingTaskMode;
    selectedTasks: OnboardingTaskList;
    customTaskPrompt: string;
    environmentClass: AsyncQuery<EnvironmentClassEntry>;
    onboardingRunner: AsyncQuery<PlainMessage<Runner>>;
    // Map of SCM host (e.g. "github.com") to the last authentication status response
    scmAuthStatuses: ScmAuthStatusMap;
    selectedRepositoryUrl: string | null;
    parsedRepositoryUrl: ParseContextURLResponse | null;
    // the "host" of the last selected SCM for authentication, e.g. "github.com"
    lastSelectedScmAuthHost: string | null;
};

export type CompletedOnboardingState = {
    selectedTasks: OnboardingTaskList;
    customTaskPrompt: string;
    selectedContext: RepoContext;
    environmentClass: EnvironmentClassEntry;
};
export const DEFAULT_SELECTED_TASKS: OnboardingTaskList = [TASK_NAME.fixBug];

// Maps renamed task values so existing localStorage entries remain valid.
const LEGACY_TASK_NAME_MAP: Record<string, string> = {
    "explain-codebase": TASK_NAME.automateEnvSetup,
};

function migrateSavedState(raw: unknown): unknown {
    if (typeof raw !== "object" || raw === null) return raw;
    const obj = raw as Record<string, unknown>;
    if (Array.isArray(obj.selectedTasks)) {
        obj.selectedTasks = obj.selectedTasks.map((t: unknown) =>
            typeof t === "string" && t in LEGACY_TASK_NAME_MAP ? LEGACY_TASK_NAME_MAP[t] : t,
        );
    }
    return obj;
}

export const onboardingStateAtom = atomWithLazy<OnboardingState>(() => {
    const savedStateJson = localStorage.getItem("onboarding-state");

    let savedState: ReturnType<typeof SavedOnboardingStateSchema.safeParse> | null;
    try {
        const parsed: unknown = savedStateJson ? JSON.parse(savedStateJson) : null;
        savedState = parsed ? SavedOnboardingStateSchema.safeParse(migrateSavedState(parsed)) : null;
    } catch {
        savedState = null;
    }

    if (savedState?.success) {
        return {
            currentStep: ONBOARDING_STEP[savedState.data.currentStep],
            taskMode: savedState.data.taskMode,
            selectedTasks: savedState.data.selectedTasks,
            customTaskPrompt: savedState.data.customTaskPrompt,
            environmentClass: { type: "pending" },
            onboardingRunner: { type: "pending" },
            scmAuthStatuses: {},
            selectedRepositoryUrl: savedState.data.selectedRepositoryUrl ?? null,
            parsedRepositoryUrl: null,
            lastSelectedScmAuthHost: null,
        };
    }

    // no saved state, or the saved state is corrupted, so start fresh here
    return {
        currentStep: ONBOARDING_STEP[ONBOARDING_STEP_SLUG.connectToScm],
        taskMode: ONBOARDING_TASK_MODE.predefined,
        selectedTasks: DEFAULT_SELECTED_TASKS,
        customTaskPrompt: "",
        environmentClass: { type: "pending" },
        onboardingRunner: { type: "pending" },
        scmAuthStatuses: {},
        selectedRepositoryUrl: null,
        parsedRepositoryUrl: null,
        lastSelectedScmAuthHost: null,
    };
});

export const customTaskPromptAtom = atom(
    (get) => get(onboardingStateAtom).customTaskPrompt,
    (get, set, customTaskPrompt: string) => {
        set(onboardingStateAtom, { ...get(onboardingStateAtom), customTaskPrompt });
    },
);

export const selectedTasksAtom = atom(
    (get) => get(onboardingStateAtom).selectedTasks,
    (get, set, selectedTasks: OnboardingTaskList) => {
        set(onboardingStateAtom, { ...get(onboardingStateAtom), selectedTasks });
    },
);

export const taskModeAtom = atom(
    (get) => get(onboardingStateAtom).taskMode,
    (get, set, taskMode: OnboardingTaskMode) => {
        set(onboardingStateAtom, { ...get(onboardingStateAtom), taskMode });
    },
);

export const currentStepAtom = atom(
    (get) => get(onboardingStateAtom).currentStep,
    (get, set, step: OnboardingStep) => {
        set(onboardingStateAtom, { ...get(onboardingStateAtom), currentStep: step });
    },
);

export const environmentClassAtom = atom(
    (get) => get(onboardingStateAtom).environmentClass,
    (get, set, environmentClass: AsyncQuery<EnvironmentClassEntry>) => {
        set(onboardingStateAtom, { ...get(onboardingStateAtom), environmentClass });
    },
);

export const scmAuthStatusesAtom = atom(
    (get) => get(onboardingStateAtom).scmAuthStatuses,
    (get, set, scmAuthStatuses: ScmAuthStatusMap) => {
        set(onboardingStateAtom, { ...get(onboardingStateAtom), scmAuthStatuses });
    },
);

export const getScmAuthStatus = (
    scmAuthStatuses: ScmAuthStatusMap,
    host: string,
): AsyncQuery<AuthenticatedWithRunnerResponse> => {
    return scmAuthStatuses[host] || { type: "pending" };
};

export const lastSelectedScmAuthHostAtom = atom(
    (get) => get(onboardingStateAtom).lastSelectedScmAuthHost,
    (get, set, lastSelectedScmAuthHost: string | null) => {
        set(onboardingStateAtom, { ...get(onboardingStateAtom), lastSelectedScmAuthHost });
    },
);

export const onboardingRunnerAtom = atom(
    (get) => get(onboardingStateAtom).onboardingRunner,
    (get, set, onboardingRunner: AsyncQuery<PlainMessage<Runner>>) => {
        set(onboardingStateAtom, { ...get(onboardingStateAtom), onboardingRunner });
    },
);

export const selectedRepositoryUrlAtom = atom(
    (get) => get(onboardingStateAtom).selectedRepositoryUrl,
    (get, set, selectedRepositoryUrl: string | null) => {
        set(onboardingStateAtom, { ...get(onboardingStateAtom), selectedRepositoryUrl });
    },
);

export const parsedRepositoryUrlAtom = atom(
    (get) => get(onboardingStateAtom).parsedRepositoryUrl,
    (get, set, parsedUrl: ParseContextURLResponse | null) => {
        set(onboardingStateAtom, { ...get(onboardingStateAtom), parsedRepositoryUrl: parsedUrl });
    },
);

export const onboardingErrorsAtom = atom<unknown[] | null>((get) => {
    const envClassQuery = get(environmentClassAtom);
    const runnerQuery = get(onboardingRunnerAtom);
    const scmAuthStatuses = get(scmAuthStatusesAtom);
    const errors = [];
    if (envClassQuery.type === "error") {
        errors.push(envClassQuery.error);
    }
    if (runnerQuery.type === "error") {
        errors.push(runnerQuery.error);
    }
    // Check for errors in any SCM auth status
    for (const status of Object.values(scmAuthStatuses)) {
        if (status.type === "error") {
            errors.push(status.error);
        }
    }
    return errors.length > 0 ? errors : null;
});

/**
 * This atom derives a prompt input submission from the onboarding state when skipping.
 * Used when the user skips the onboarding.
 * The submission has no context selected, allowing the user to choose their own context.
 */
export const skipOnboardingPromptInputSubmissionAtom = atom<PromptInputSubmission | null>((get) => {
    const prompt = get(customTaskPromptAtom);
    const selectedTasks = get(selectedTasksAtom);
    const environmentClass = get(environmentClassAtom);
    const taskMode = get(taskModeAtom);
    if ((prompt || selectedTasks.length > 0) && environmentClass.type === "success") {
        return {
            contexts: [],
            prompts:
                taskMode === ONBOARDING_TASK_MODE.custom
                    ? [prompt]
                    : selectedTasks.map((task) => TASK_DESCRIPTIONS[task].prompt),
            environmentClass: environmentClass.data,
        };
    }
    return null;
});

export const promptInputSubmissionAtom = atom<AsyncQuery<PromptInputSubmission>>((get) => {
    const prompt = get(customTaskPromptAtom);
    const selectedTasks = get(selectedTasksAtom);
    const taskMode = get(taskModeAtom);
    const environmentClass = get(environmentClassAtom);
    const parsedRepositoryUrl = get(parsedRepositoryUrlAtom);
    if ((prompt || selectedTasks.length > 0) && environmentClass.type === "success" && parsedRepositoryUrl?.git) {
        return {
            type: "success",
            data: {
                prompts:
                    taskMode === ONBOARDING_TASK_MODE.custom
                        ? [prompt]
                        : selectedTasks.map((task) => TASK_DESCRIPTIONS[task].prompt),
                contexts: [
                    {
                        kind: "repo",
                        url: parsedRepositoryUrl.originalContextUrl,
                        branchName: parsedRepositoryUrl.git.branch,
                        repoName: parsedRepositoryUrl.git.repo,
                        ownerName: parsedRepositoryUrl.git.owner,
                    },
                ],
                environmentClass: environmentClass.data,
                templateTitles:
                    // user provided custom prompt so can't provide a conversation title
                    taskMode === ONBOARDING_TASK_MODE.custom
                        ? undefined
                        : selectedTasks.map((task) => TASK_DESCRIPTIONS[task].title),
            },
        };
    }
    return {
        type: "pending",
    };
});

export function useSaveOnboardingStateEffect() {
    const saveStateToLocalStorage = useDebouncedCallback((onboardingState: OnboardingState) => {
        const savedState: SavedOnboardingState = {
            currentStep: onboardingState.currentStep.slug,
            taskMode: onboardingState.taskMode,
            selectedTasks: onboardingState.selectedTasks,
            customTaskPrompt: onboardingState.customTaskPrompt,
            selectedRepositoryUrl: onboardingState.selectedRepositoryUrl,
        };
        localStorage.setItem("onboarding-state", JSON.stringify(savedState));
    }, 1000);

    useAtom(
        useMemo(
            () =>
                atomEffect((get) => {
                    const onboardingState = get(onboardingStateAtom);
                    saveStateToLocalStorage(onboardingState);
                }),
            [saveStateToLocalStorage],
        ),
    );
}
